import { IGroupedComboBoxGroup, IGroupedComboBoxItem } from 'grouped-combobox-core';
import { assert } from '../../common';
import { DefaultMaximumMruEntries } from '../../common/constants';
import { DefaultStringResources } from '../../common/defaultStringResources';
import { isThemeFont } from '../../common/fontUtils';
import { createFontItem } from './createFontItem';
import { GroupCreationData, GroupedComboBoxRenderers } from './createGroups.types';
import { createMruItem } from './createMruItem';

export function createGroups(
    data: GroupCreationData,
    renderers: GroupedComboBoxRenderers
): IGroupedComboBoxGroup[] {
    const {
        fonts,
        initialPinnedFontKeys = [],
        mruList = [],
        stringResources = DefaultStringResources,
        isSubscriber,
        maximumMruEntries = DefaultMaximumMruEntries,
        allowFontInfoPane = true,
        allowPinning = true,
    } = data;

    const { renderPreviewFontLabel, renderOwnFontLabel, renderHeaderLabel, renderIcon } = renderers;

    const sortedFonts = fonts.sort((a, b) => a.fontKey.localeCompare(b.fontKey));

    const nonOrganizationItems = sortedFonts
        .filter((f) => f.fontType !== 'organization')
        .map<IGroupedComboBoxItem>((font) =>
            createFontItem(
                font,
                initialPinnedFontKeys,
                isSubscriber,
                renderPreviewFontLabel,
                renderIcon,
                allowFontInfoPane,
                allowPinning,
                stringResources
            )
        );

    let numberOfThemeFonts = mruList.findIndex((item) => !isThemeFont(item));
    if (numberOfThemeFonts === -1) {
        numberOfThemeFonts = mruList.length;
    }

    const groups: IGroupedComboBoxGroup[] = [
        // MRU Section
        {
            text: stringResources.mruSectionLabel,
            groupKey: 'MRU',
            items: mruList
                .slice(0, maximumMruEntries + numberOfThemeFonts)
                .map<IGroupedComboBoxItem>((entry) =>
                    createMruItem(entry, renderPreviewFontLabel, renderIcon, nonOrganizationItems)
                ),
        },
    ];

    // Pinned section
    if (isSubscriber && allowPinning) {
        groups.push({
            text: stringResources.pinnedSectionLabel,
            groupKey: 'Pinned',
            items: nonOrganizationItems.filter(
                (item) => item.itemKey && initialPinnedFontKeys.includes(item.itemKey)
            ),
        });
    }

    if (isSubscriber) {
        const organizationFonts = sortedFonts.filter((f) => f.fontType === 'organization');
        if (organizationFonts.length > 0) {
            assert(stringResources.organizationSectionLabel);
            // Organization fonts section
            groups.push({
                text: stringResources.organizationSectionLabel,
                groupKey: 'Organization',
                items: organizationFonts.map<IGroupedComboBoxItem>((font) =>
                    createFontItem(
                        font,
                        initialPinnedFontKeys,
                        isSubscriber,
                        renderOwnFontLabel,
                        renderIcon,
                        allowFontInfoPane,
                        allowPinning,
                        stringResources
                    )
                ),
            });
        }
    }

    // All fonts section
    groups.push({
        text: stringResources.allFontsSectionLabel,
        groupKey: 'All',
        initiallyCollapsed: true,
        items: nonOrganizationItems,
        onRenderHeaderLabel: renderHeaderLabel,
    });

    return groups;
}
